import React from 'react';

import { AspectRatio, Flex, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAdjustmentsHorizontal, IconClockBolt, IconGift, IconRocket } from '@tabler/icons-react';
import bgImage from 'assets/images/bg.png';
import bgGradient from 'assets/images/bg_gradient.png';
import { Branding } from 'components/Branding';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { PayWallPage } from 'pages/PayWall/PayWallPage';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { MIN_SCREEN_WIDTH, YOUTUBE_DEMO_VIDEO_ID } from 'shared/constants/general-constants';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { ProductName } from 'shared/interfaces/Product';
import { MBox } from 'shared/motion/MBox';
import { MPaper } from 'shared/motion/MPaper';

// We use this to delay the animations so all animations are clean and not affected by the initial load
const ANIMATION_BASE_DELAY_SECONDS = 0.3;

const EXCLUDED_ROUTES = [
  ROUTE_LINKS.googleDriveAuthRedirectHandler,
  ROUTE_LINKS.instagramAuthPopupRedirectHandler,
  ROUTE_LINKS.postLoginRouter,
  ROUTE_LINKS.onboarding,
  ROUTE_LINKS.subscriptionRedirectHandler,
  ROUTE_LINKS.postBillingSessionHandler
];

export const ScreenSizeRouteGuard = () => {
  const { t } = useTranslation();
  const aboveMinWidth = useMediaQuery(`(min-width: ${MIN_SCREEN_WIDTH}px)`);
  const [subscription] = useSubscriptionContext();
  const isFreeAccount = subscription.product.name === ProductName.STARTER;

  if (aboveMinWidth === undefined) return <FullScreenLoader />;

  return !aboveMinWidth && !EXCLUDED_ROUTES.includes(window.location.pathname) ? (
    isFreeAccount ? (
      <PayWallPage />
    ) : (
      <Flex
        h="100svh"
        w="100vw"
        direction="column"
        align="center"
        justify="center"
        pos="relative"
        sx={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',

          backgroundPosition: 'center'
        }}>
        <MPaper
          withBorder
          shadow="sm"
          radius="lg"
          p={28}
          mah={800}
          w="100%"
          maw={400}
          style={{
            backgroundColor: '#252525',
            backgroundImage: `url(${bgGradient})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            color: 'white'
          }}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            delay: ANIMATION_BASE_DELAY_SECONDS,
            duration: 0.5,
            ease: [0.19, 1.0, 0.22, 1.0]
          }}>
          <MBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: ANIMATION_BASE_DELAY_SECONDS + 0.2, duration: 0.3 }}>
            <Branding isLight width={150} />
          </MBox>

          <MBox
            mt="md"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: ANIMATION_BASE_DELAY_SECONDS + 0.3, duration: 0.4 }}>
            <Title order={4}>{t('APP.SCREEN_TOO_SMALL.HEADER.TITLE')}</Title>
            <Text mt="sm">{t('APP.SCREEN_TOO_SMALL.HEADER.DESCRIPTION')}</Text>
          </MBox>
          <MBox
            mt="lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: ANIMATION_BASE_DELAY_SECONDS + 0.4, duration: 0.3 }}>
            <Title order={4}>{t('APP.SCREEN_TOO_SMALL.BENEFITS.TITLE')}</Title>
          </MBox>
          {[
            { Icon: IconClockBolt, point: 'ONE' },
            { Icon: IconAdjustmentsHorizontal, point: 'TWO' },
            { Icon: IconGift, point: 'THREE' },
            { Icon: IconRocket, point: 'FOUR' }
          ].map(({ Icon, point }, index) => (
            <MBox
              key={point}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: ANIMATION_BASE_DELAY_SECONDS + 0.5 + index * 0.1,
                duration: 0.3
              }}
              whileHover={{ x: 5 }}>
              <Flex direction="row" align="flex-start" mt={index === 0 ? 'sm' : 'lg'} gap="sm">
                <Icon style={{ width: 22, height: 22, flexShrink: 0 }} />
                <Text>
                  <Text fw={600} fz="0.9rem" span inherit>
                    {t(`APP.SCREEN_TOO_SMALL.BENEFITS.POINT_${point}.TITLE`)}
                  </Text>
                  {t(`APP.SCREEN_TOO_SMALL.BENEFITS.POINT_${point}.DESCRIPTION`)}
                </Text>
              </Flex>
            </MBox>
          ))}

          <MBox
            mt="lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: ANIMATION_BASE_DELAY_SECONDS + 0.9, duration: 0.4 }}>
            <Title order={4}>{t('APP.SCREEN_TOO_SMALL.BENEFITS.VIDEO.TITLE')}</Title>
            <AspectRatio mt="sm" ratio={16 / 9}>
              <iframe
                style={{ border: 'none', borderRadius: 16 }}
                src={`https://www.youtube.com/embed/${YOUTUBE_DEMO_VIDEO_ID}`}
                title="Droxy Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </AspectRatio>
          </MBox>
        </MPaper>
      </Flex>
    )
  ) : (
    <Outlet />
  );
};
